import React, { useContext, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthContext } from "../../../../config/AuthContext";
import { FloatButton, Table, Tag, Card, Select, Button, Tooltip, Switch, Space, Divider } from "antd";
import { TableProps } from "antd/es/table";
import { BiRefresh, BiDownload } from "react-icons/bi";
import { BsEye } from "react-icons/bs";
import { SettingOutlined } from '@ant-design/icons';
import JutgeColors from "../../../../styles/JutgeColors";
import AvaluacioColors from "../../../../styles/AvaluacioColors";
import { GetUsuarisIdBbddNotes } from "../../../../dao/bbdd/GetUsuarisIdBbddNotes";
import { GetUsuarisIdAulaIdBbddNotes } from "../../../../dao/bbdd/GetUsuarisIdAulaIdBbddNotes";
import { GetBbddPracticaIdImagen } from "../../../../dao/bbdd/GetBbddPracticaIdImagen";
import { GetBbddActivitats } from "../../../../dao/bbdd/GetBbddActivitats";
import AppSider from "../../components/AppSider";
import { Content } from "antd/es/layout/layout";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ConfiguracioModal from "../../../jutge/components/ConfiguracioModal";
import { GetAulaRaConfig } from '../../../../dao/GetAulaRaConfig';

const { Option } = Select;

const Notes = () => {
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);

    const [dades, setDades] = useState<any[]>([]);
    const [bbddActivitats, setBbddActivitats] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [pantalla, setPantalla] = useState(0);
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [subData, setSubData] = useState<Record<string, any[]>>({});
    const [selectedAula, setSelectedAula] = useState<number | null>(null);
    const [selectedRA, setSelectedRA] = useState<string | null>(null);
    const [selectedInst, setSelectedInst] = useState<string | null>(null);
    const [excelData, setExcelData] = useState<any>(null);
    const [configModalVisible, setConfigModalVisible] = useState(false);
    const [raConfig, setRaConfig] = useState<any[]>([]);
    const [showPractiques, setShowPractiques] = useState(false);

    useEffect(() => { if (isLoggedIn) checkDades(); }, []);

    const aulaOptions = useMemo(() => {
        const map = new Map<number, string>();
        dades.flatMap(a => a.practiques || []).forEach((p: any) => {
            if (p && p.id_aula) {
                map.set(Number(p.id_aula), p.aula || `Aula ${p.id_aula}`);
            }
        });
        bbddActivitats.forEach((act: any) => {
            if (act && act.id_aula) {
                map.set(Number(act.id_aula), act.aula || `Aula ${act.id_aula}`);
            }
        });
        return Array.from(map.entries()).map(([id, nom]) => ({ id, nom }));
    }, [dades, bbddActivitats]);

    // Auto-select first aula
    useEffect(() => {
        if (aulaOptions.length > 0 && selectedAula === null) {
            onSelectAula(aulaOptions[0].id);
        }
    }, [aulaOptions]);

    async function checkDades() {
        setLoading(true);
        try {
            const [data, actsRaw] = await Promise.all([
                GetUsuarisIdBbddNotes(userId),
                GetBbddActivitats(userId)
            ]);
            setDades(Array.isArray(data) ? data : []);

            let parsedActs: any[] = [];
            if (Array.isArray(actsRaw)) {
                parsedActs = actsRaw;
            } else if (typeof actsRaw === 'string') {
                try { parsedActs = JSON.parse(actsRaw); } catch { parsedActs = []; }
            } else if (actsRaw && (actsRaw as any).json) {
                try {
                    parsedActs = typeof (actsRaw as any).json === 'string'
                        ? JSON.parse((actsRaw as any).json)
                        : (actsRaw as any).json;
                } catch { parsedActs = []; }
            }
            setBbddActivitats(Array.isArray(parsedActs) ? parsedActs : []);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);

        setBreadCrumb([
            { nom: 'Home', nav: '/home' },
            { nom: 'database', nav: '/database' },
            { nom: 'notes', nav: `/database/notes` }
        ], true);
    }

    const handleExpand = async (expanded: boolean, record: any) => {
        const id = record.id_usuari;
        if (expanded) {
            if (!subData[id]) {
                const practiques = (record.practiques || []).map((p: any) => ({ ...p, imagen: null }));
                setSubData(prev => ({ ...prev, [id]: practiques }));
                practiques.forEach(async (p: any, index: number) => {
                    try {
                        const res: any = await GetBbddPracticaIdImagen(p.id_practica + "");
                        const cleaned = res?.imagen?.split(':').pop() || null;
                        setSubData(prev => {
                            const updated = [...(prev[id] || [])];
                            updated[index] = { ...updated[index], imagen: cleaned };
                            return { ...prev, [id]: updated };
                        });
                    } catch { }
                });
            }
            setExpandedRowKeys([id]);
        } else setExpandedRowKeys([]);
    };

    async function onSelectAula(idAula: number | undefined) {
        if (!idAula) {
            setSelectedAula(null);
            setExcelData(null);
            setRaConfig([]);
            return;
        }
        setSelectedAula(idAula);
        setLoading(true);

        try {
            const [data, config] = await Promise.all([
                GetUsuarisIdAulaIdBbddNotes(userId + "", idAula + ""),
                GetAulaRaConfig(idAula + "")
            ]);
            setExcelData(data);
            setRaConfig(config || []);
        } catch (e) {
            console.error(e);
        }

        setLoading(false);
    }

    const excelColumns: any[] = useMemo(() => {
        if (!excelData?.practiques) return [];

        let currentPractiques = excelData.practiques;

        if (selectedRA) {
            currentPractiques = currentPractiques.filter((p: any) =>
                p.ras?.some((r: any) => r.nom === selectedRA)
            );
        }

        if (selectedInst) {
            currentPractiques = currentPractiques.filter((p: any) => p.inst === selectedInst);
        }

        const allRas: string[] = (excelData.ras_aula || []).map((ra: any) => ra.nom);
        allRas.sort();

        const displayedRas = selectedRA ? [selectedRA] : allRas;

        // Color palette per RA
        const raHeaderColors = ['#389e0d', '#d46b08', '#096dd9', '#c41d7f', '#08979c', '#7c3aed'];
        const raUiColors = ['#f6ffed', '#fffbe6', '#e6f7ff', '#fff0f6', '#e6fffb', '#f5f0ff'];
        const raBorderColors = ['#b7eb8f', '#ffd591', '#91caff', '#ffadd2', '#87e8de', '#d3adf7'];

        // RA grouped columns
        const raGroupCols = displayedRas.map((ra, raIdx) => {
            const bgColor = raUiColors[raIdx % raUiColors.length];
            const headerColor = raHeaderColors[raIdx % raHeaderColors.length];
            const borderColor = raBorderColors[raIdx % raBorderColors.length];
            let raInstruments: string[] = [];
            const configItems = raConfig.filter((c: any) => c.nom_ra === ra);

            if (configItems.length > 0) {
                raInstruments = configItems.map((c: any) => c.instrument);
            } else {
                const practicesWithRa = excelData.practiques.filter((p: any) =>
                    p.ras?.some((r: any) => r.nom === ra)
                );
                raInstruments = Array.from(new Set(practicesWithRa.map((p: any) => p.inst))).filter(Boolean) as string[];
                raInstruments.sort();
            }

            if (selectedInst) raInstruments = raInstruments.includes(selectedInst) ? [selectedInst] : [];

            const gradeTag = (val: number | null, bold = false) => val === null
                ? <Tag color="default" style={{ margin: 0 }}>N/P</Tag>
                : <Tag
                    color={val >= 5 ? 'success' : 'error'}
                    style={{ fontWeight: bold ? 700 : 500, margin: 0, minWidth: 44, textAlign: 'center' }}
                >
                    {val.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Tag>;

            const instChildren = raInstruments.map(inst => ({
                title: <Tooltip title={`${inst} (RA ${ra}) · clic para filtrar`}>
                    <div
                        style={{
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                            cursor: 'pointer',
                            color: (selectedInst === inst && selectedRA === ra) ? headerColor : '#595959',
                            fontWeight: (selectedInst === inst && selectedRA === ra) ? 700 : 400,
                            fontSize: 11,
                            maxHeight: 90,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        onClick={() => { if (selectedInst === inst && selectedRA === ra) { setSelectedInst(null); setSelectedRA(null); } else { setSelectedRA(ra); setSelectedInst(inst); } }}
                    >{inst}</div>
                </Tooltip>,
                dataIndex: `ra_${ra}_inst_${inst}`,
                key: `ra_${ra}_inst_${inst}`,
                align: 'center' as const,
                width: 80,
                onHeaderCell: () => ({ style: { background: bgColor, borderBottom: `2px solid ${borderColor}` } }),
                onCell: () => ({ style: { background: bgColor } }),
                render: (val: any) => gradeTag(val)
            }));

            const totalChild = {
                title: <Tooltip title="Nota total ponderada del RA. Clic para filtrar">
                    <span
                        style={{ cursor: 'pointer', color: headerColor, fontWeight: 700, fontSize: 12 }}
                        onClick={() => { if (selectedRA === ra && !selectedInst) setSelectedRA(null); else { setSelectedRA(ra); setSelectedInst(null); } }}
                    >Total</span>
                </Tooltip>,
                dataIndex: `ra_${ra}`,
                key: `ra_${ra}_total`,
                align: 'center' as const,
                width: 90,
                onHeaderCell: () => ({ style: { background: bgColor, borderBottom: `2px solid ${borderColor}`, fontWeight: 'bold' } }),
                onCell: () => ({ style: { background: bgColor } }),
                render: (val: any) => {
                    if (val === null) return <Tag color="default" style={{ margin: 0 }}>N/P</Tag>;
                    const score = typeof val === 'object' ? val.value : val;
                    const isCapped = typeof val === 'object' ? val.isCapped : false;
                    const isFailed = typeof val === 'object' ? val.isFailed : false;
                    const isMaxCapped = typeof val === 'object' ? val.isMaxCapped : false;
                    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        {isCapped && <Tooltip title="Nota truncada a 4: no s'ha superat el mínim d'algun instrument"><span style={{ fontSize: 11, cursor: 'help' }}>⚠️</span></Tooltip>}
                        {!isCapped && isFailed && <Tooltip title="Nota afectada pel mínim: un instrument no ha assolit la nota mínima"><span style={{ fontSize: 11, cursor: 'help' }}>🔴</span></Tooltip>}
                        {isMaxCapped && <Tooltip title="Nota limitada per la Nota Màxima configurada per aquesta prova"><span style={{ fontSize: 11, cursor: 'help' }}>⬇️</span></Tooltip>}
                        {gradeTag(score, true)}
                    </div>;
                }
            };

            const finalChild = {
                title: <Tooltip title="Nota final redondeada"><span style={{ color: headerColor, fontWeight: 700, fontSize: 12 }}>Fin</span></Tooltip>,
                dataIndex: `ra_${ra}_round`,
                key: `ra_${ra}_round`,
                align: 'center' as const,
                width: 60,
                onHeaderCell: () => ({ style: { background: bgColor, borderBottom: `2px solid ${borderColor}`, fontWeight: 'bold' } }),
                onCell: () => ({ style: { background: bgColor } }),
                render: (_: any, record: any) => {
                    const baseVal = record[`ra_${ra}`];
                    const grade = typeof baseVal === 'object' ? baseVal?.value : baseVal;
                    if (grade === null || grade === undefined) return <Tag color="default" style={{ margin: 0 }}>N/P</Tag>;
                    const rounded = grade < 5 ? Math.floor(grade) : Math.round(grade);
                    return <Tag color={rounded >= 5 ? 'success' : 'error'} style={{ fontWeight: 800, fontSize: 14, margin: 0, minWidth: 32, textAlign: 'center' }}>{rounded}</Tag>;
                }
            };

            return {
                title: <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                    <span style={{ fontWeight: 800, color: headerColor, fontSize: 13 }}>RA {ra}</span>
                </div>,
                key: `ra_${ra}_group`,
                onHeaderCell: () => ({ style: { background: bgColor, borderBottom: `3px solid ${borderColor}`, textAlign: 'center' } }),
                children: [...instChildren, totalChild, finalChild]
            };
        });

        // Practice columns (hidden by default, shown with toggle)
        const practiceCols = currentPractiques.map((p: any) => ({
            title: <Tooltip title={p.nom}><div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 11, maxHeight: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nom}</div></Tooltip>,
            dataIndex: `p_${p.id}`,
            key: `p_${p.id}`,
            align: 'center' as const,
            width: 50,
            onHeaderCell: () => ({ style: { background: '#fafafa', verticalAlign: 'bottom', padding: '4px 2px' } }),
            render: (e: any) => e === null
                ? <Tag color="default" style={{ margin: 0, padding: '0 4px', fontSize: 10 }}>—</Tag>
                : <Tag color={e >= 5 ? 'success' : 'error'} style={{ margin: 0, padding: '0 4px', fontSize: 10 }}>{e.toFixed(1)}</Tag>
        }));

        const alumneCol = {
            title: <span style={{ fontWeight: 700, color: '#262626' }}>Alumne</span>,
            dataIndex: 'alumne',
            key: 'alumne',
            fixed: 'left' as const,
            width: 200,
            render: (e: string) => <span style={{ fontWeight: 600, fontSize: 13, color: '#262626' }}>{e}</span>
        };

        return [
            alumneCol,
            ...(showPractiques ? [{ title: <span style={{ color: '#8c8c8c', fontSize: 11 }}>Prácticas individuales</span>, key: 'pracs_group', children: practiceCols }] : []),
            ...raGroupCols
        ];
    }, [excelData, selectedRA, selectedInst, raConfig, showPractiques]);

    const excelRows: any[] = useMemo(() => {
        if (!excelData) return [];
        const mapNotas = new Map<string, any>((excelData.notes || []).map((n: any) => [`${n.id_usuari}-${n.id_practica}`, n.nota]));

        // Map notes RA
        const mapNotasRa = new Map((excelData.notes_ra || []).map((n: any) => [`${n.id_usuari}-${n.nom_ra}`, n.nota]));

        const instruments: string[] = Array.from(new Set(
            (excelData.practiques || []).filter((p: any) => p.inst).map((p: any) => p.inst)
        )) as string[];
        instruments.sort();

        // Use all RAs from backend response
        const ras: string[] = (excelData.ras_aula || []).map((ra: any) => ra.nom);
        ras.sort();

        const studentRows = (excelData.alumnes || []).map((a: any) => {
            const row: any = { key: a.id, alumne: `${a.cognoms}, ${a.nom}` };
            (excelData.practiques || []).forEach((p: any) => {
                const note = mapNotas.get(`${a.id}-${p.id}`);
                row[`p_${p.id}`] = (note !== null && note !== undefined) ? (Number(note) / 10) : null;
            });

            instruments.forEach((inst: string) => {
                const practsInst = (excelData.practiques || []).filter((p: any) => p.inst === inst);
                const notas = practsInst.map((p: any) => mapNotas.get(`${a.id}-${p.id}`)).filter((n: any) => n !== null && n !== undefined);
                row[`inst_${inst}`] = notas.length === 0 ? null : (notas.reduce((acc: number, n: number) => acc + n, 0) / notas.length) / 10;
            });

            ras.forEach((ra: string) => {
                let raTotal: any = 0;
                let calculated = false;

                const configItems = raConfig.filter((c: any) => c.nom_ra === ra);

                let failedInstrument = false;
                let isRowMaxCapped = false;

                if (configItems.length > 0) {
                    const totalWeight = configItems.filter(c => !c.isRecovery).reduce((sum, c) => sum + (c.pes || 0), 0);

                    if (totalWeight === 0) {
                        const dbVal = mapNotasRa.get(`${a.id}-${ra}`);
                        if (dbVal !== undefined) {
                            row[`ra_${ra}`] = { value: dbVal, isCapped: false };
                            configItems.forEach((conf: any) => {
                                row[`ra_${ra}_inst_${conf.instrument}`] = null;
                            });
                        } else {
                            const raPractices = (excelData.practiques || []).filter((p: any) =>
                                p.ras?.some((r: any) => r.nom === ra)
                            );
                            let sum = 0;
                            let count = 0;
                            raPractices.forEach((p: any) => {
                                const note = mapNotas.get(`${a.id}-${p.id}`);
                                if (note !== null && note !== undefined) {
                                    sum += Number(note);
                                    count++;
                                }
                            });
                            const avg = count > 0 ? (sum / count) / 10 : null;
                            row[`ra_${ra}`] = avg !== null ? { value: avg, isCapped: false } : null;
                            configItems.forEach((conf: any) => {
                                row[`ra_${ra}_inst_${conf.instrument}`] = null;
                            });
                        }
                        return;
                    }

                    calculated = true;

                    const instGrades: Record<string, number | null> = {};

                    configItems.forEach((conf: any) => {
                        const inst = conf.instrument;
                        const practices = (excelData.practiques || []).filter((p: any) =>
                            p.ras?.some((r: any) => r.nom === ra && r.instrument === inst)
                        );

                        let instAvg: number | null = null;
                        if (practices.length > 0) {
                            const grades = practices.map((p: any) => mapNotas.get(`${a.id}-${p.id}`)).filter((n: any) => n !== null && n !== undefined);
                            if (grades.length > 0) {
                                instAvg = (grades.reduce((acc: number, n: number) => acc + n, 0) / grades.length) / 10;
                            }
                        }
                        instGrades[inst] = instAvg;
                        row[`ra_${ra}_inst_${inst}`] = instAvg;
                    });

                    const modifiedGrades = { ...instGrades };
                    let finalGradeOverride: number | null = null;
                    let finalGradeCapped = false;
                    let finalGradeMaxCapped = false;

                    configItems.forEach((item: any) => {
                        if (!item.isRecovery) return;

                        let recGrade = instGrades[item.instrument];
                        if (recGrade === null || recGrade === undefined || recGrade <= 0) return;

                        if (recGrade > 10) recGrade /= 10;

                        if (item.recoveryType === 'final_grade') {
                            const rawCap = item.notaMaxima;
                            const cap = (rawCap !== null && rawCap !== undefined && rawCap !== '') ? parseFloat(rawCap) : null;
                            let computedGrade = recGrade;
                            let computedMaxCapped = false;

                            if (cap !== null && !isNaN(cap) && recGrade > cap) {
                                computedGrade = cap;
                                computedMaxCapped = true;
                            }

                            if (finalGradeOverride !== null && finalGradeOverride >= 5 && computedGrade < finalGradeOverride) {
                                // Mantenemos la nota aprobada
                            } else {
                                finalGradeOverride = computedGrade;
                                finalGradeMaxCapped = computedMaxCapped;
                                finalGradeCapped = finalGradeOverride < (item.minim || 0);
                            }
                        } else {
                            const target = item.targetInstrument || 'Prueba Practica';
                            const currentGrade = modifiedGrades[target] ?? 0;

                            if (item.recoveryType === 'max') {
                                modifiedGrades[target] = Math.max(currentGrade, recGrade);
                            } else if (item.recoveryType === 'average') {
                                modifiedGrades[target] = (currentGrade + recGrade) / 2;
                            } else if (item.recoveryType === 'replace') {
                                modifiedGrades[target] = recGrade;
                            }
                        }
                    });

                    configItems.forEach((conf: any) => {
                        if (conf.isRecovery) return;

                        const grade = modifiedGrades[conf.instrument];

                        if (grade !== null && grade !== undefined) {
                            const min = conf.minim || 0;
                            if (grade < min) {
                                failedInstrument = true;
                            }
                            raTotal += (grade * ((conf.pes || 0) / 100));
                            calculated = true;
                        }
                    });

                    if (finalGradeOverride !== null && finalGradeOverride > raTotal) {
                        raTotal = finalGradeOverride;
                        failedInstrument = finalGradeCapped;
                        isRowMaxCapped = finalGradeMaxCapped;
                        calculated = true;
                    }

                    if (!calculated && finalGradeOverride === null) {
                        const raPractices = (excelData.practiques || []).filter((p: any) =>
                            p.ras?.some((r: any) => r.nom === ra)
                        );
                        let hasAnySubmission = false;
                        for (const p of raPractices) {
                            const note = mapNotas.get(`${a.id}-${p.id}`);
                            if (note !== null && note !== undefined) {
                                hasAnySubmission = true;
                                break;
                            }
                        }
                        if (hasAnySubmission) {
                            calculated = true;
                        }
                    }

                } else {
                    const raPractices = (excelData.practiques || []).filter((p: any) => p.ras?.some((r: any) => r.nom === ra));
                    const raInsts = Array.from(new Set(
                        raPractices.flatMap((p: any) =>
                            (p.ras || []).filter((r: any) => r.nom === ra).map((r: any) => r.instrument)
                        )
                    )).filter(Boolean) as string[];

                    let tempTotal = 0;
                    let instCount = 0;

                    raInsts.forEach((inst) => {
                        const practices = raPractices.filter((p: any) =>
                            p.ras?.some((r: any) => r.nom === ra && r.instrument === inst)
                        );

                        const grades = practices.map((p: any) => mapNotas.get(`${a.id}-${p.id}`)).filter((n: any) => n !== null && n !== undefined);
                        if (grades.length > 0) {
                            const avg = (grades.reduce((acc: number, n: number) => acc + n, 0) / grades.length) / 10;
                            row[`ra_${ra}_inst_${inst}`] = avg;
                            tempTotal += avg;
                            instCount++;
                        } else {
                            row[`ra_${ra}_inst_${inst}`] = null;
                        }
                    });

                    const dbVal = mapNotasRa.get(`${a.id}-${ra}`);
                    if (dbVal !== undefined) {
                        raTotal = dbVal;
                        calculated = true;
                    } else if (instCount > 0) {
                        raTotal = tempTotal / instCount;
                        calculated = true;
                    }
                }

                let isCapped = false;
                if (calculated && failedInstrument) {
                    if (raTotal > 4) {
                        raTotal = 4;
                        isCapped = true;
                    }
                }

                row[`ra_${ra}`] = calculated ? { value: raTotal, isCapped, isFailed: failedInstrument, isMaxCapped: isRowMaxCapped } : null;
            });
            return row;
        });

        return [...studentRows];
    }, [excelData, raConfig]);

    const columns: any[] = [
        { key: 'id_usuari', title: 'ID', dataIndex: 'id_usuari', width: 40, render: (e: string) => <Tag color={JutgeColors.colors.color4}>{e}</Tag> },
        { key: 'nom', title: 'Alumne', render: (_: any, row: any) => <Tag color={JutgeColors.colors.color1}>{row.cognoms}, {row.nom}</Tag> },
        {
            key: 'aulas', title: 'Aulas', dataIndex: 'practiques', render: (practiques: any[]) => {
                if (!practiques || !Array.isArray(practiques)) return null;
                const colors = Object.values(JutgeColors.colors);
                const uniqueAulas = Array.from(new Set(practiques.map(p => p.id_aula).filter(Boolean)));
                return <>{uniqueAulas.map((id: number) => { const aula = practiques.find(p => p.id_aula === id)?.aula; return <Tag key={id} color={colors[id % colors.length]}>{aula ?? id}</Tag>; })}</>;
            }
        }
    ];

    const subColumns: any[] = [
        { key: 'imagen', title: 'Imatge', dataIndex: 'imagen', render: (_img: any, record: any) => record.imagen ? <img src={`data:image/png;base64,${record.imagen}`} style={{ width: 50, height: 50, borderRadius: 12 }} /> : <div style={{ width: 50, height: 50, background: '#eee', borderRadius: 12 }} /> },
        { key: 'nom', title: 'Pràctica', dataIndex: 'nom', render: (e: string) => <Tag color="blue">{e}</Tag> },
        { key: 'nota', title: 'Nota', dataIndex: 'nota', align: 'center', render: (e: number) => e === null || e === undefined ? <Tag color="purple">N/P</Tag> : <Tag color={e >= 50 ? "green" : "red"}>{(e / 10).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Tag> }
    ];

    const tableProps: TableProps<any> = { loading };

    const downloadExcel = async () => {
        if (!excelData || !excelRows) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Notes");

        const ras: string[] = (excelData.ras_aula || []).map((ra: any) => ra.nom);
        ras.sort();

        const raColors = [
            'FFE2EFDA',
            'FFFFF2CC',
            'FFDDEBF7',
            'FFFCE4D6',
            'FFF2F2F2',
            'FFE1D5E7'
        ];

        let columns: any[] = [
            { header: "Alumne", key: "alumne", width: 35 }
        ];

        (excelData.practiques || []).forEach((p: any) => {
            columns.push({ header: p.nom, key: `p_${p.id}`, width: 12 });
        });

        ras.forEach(ra => {
            const configItems = raConfig.filter((c: any) => c.nom_ra === ra);
            let raInstruments: string[] = [];
            if (configItems.length > 0) {
                raInstruments = configItems.map((c: any) => c.instrument);
            } else {
                const practicesWithRa = (excelData.practiques || []).filter((p: any) =>
                    p.ras?.some((r: any) => r.nom === ra)
                );
                raInstruments = Array.from(new Set(practicesWithRa.map((p: any) => p.inst))).filter(Boolean) as string[];
                raInstruments.sort();
            }

            raInstruments.forEach(inst => {
                columns.push({ header: `${inst} (${ra})`, key: `ra_${ra}_inst_${inst}`, width: 15 });
            });

            columns.push({ header: `RA ${ra}`, key: `ra_${ra}`, width: 12 });
            columns.push({ header: `RA ${ra} (Fin)`, key: `ra_${ra}_round`, width: 12 });
        });

        worksheet.columns = columns;

        const weightsRow: any = { alumne: "PES / PES TOTAL (%)" };

        ras.forEach(ra => {
            const configItems = raConfig.filter((c: any) => c.nom_ra === ra);
            configItems.forEach(conf => {
                weightsRow[`ra_${ra}_inst_${conf.instrument}`] = conf.pes !== undefined ? `${conf.pes}%` : "";
            });
            weightsRow[`ra_${ra}`] = "100%";
        });
        const wRow = worksheet.addRow(weightsRow);
        wRow.font = { italic: true, bold: true, color: { argb: 'FF555555' } };

        excelRows.forEach((row, rowIndex) => {
            const dataRow: any = { alumne: row.alumne };

            (excelData.practiques || []).forEach((p: any) => {
                dataRow[`p_${p.id}`] = row[`p_${p.id}`];
            });

            ras.forEach(ra => {
                const configItems = raConfig.filter((c: any) => c.nom_ra === ra);
                let raInstruments: string[] = [];
                if (configItems.length > 0) {
                    raInstruments = configItems.map((c: any) => c.instrument);
                } else {
                    const practicesWithRa = (excelData.practiques || []).filter((p: any) =>
                        p.ras?.some((r: any) => r.nom === ra)
                    );
                    raInstruments = Array.from(new Set(practicesWithRa.map((p: any) => p.inst))).filter(Boolean) as string[];
                    raInstruments.sort();
                }

                raInstruments.forEach(inst => {
                    const practices = (excelData.practiques || []).filter((p: any) =>
                        p.ras?.some((r: any) => r.nom === ra && r.instrument === inst)
                    );

                    if (practices.length > 0) {
                        const pCols = practices.map((p: any) => {
                            const col = columns.findIndex(c => c.key === `p_${p.id}`) + 1;
                            const colLetter = worksheet.getColumn(col).letter;
                            return `${colLetter}${rowIndex + 3}`;
                        });
                        dataRow[`ra_${ra}_inst_${inst}`] = { formula: `AVERAGE(${pCols.join(",")})` };
                    } else {
                        dataRow[`ra_${ra}_inst_${inst}`] = row[`ra_${ra}_inst_${inst}`];
                    }
                });

                if (configItems.length > 0) {
                    const finalRec = configItems.find(c => c.isRecovery && c.recoveryType === 'final_grade');
                    const baseInstruments = configItems.filter(c => !c.isRecovery && c.pes > 0);

                    const formulaParts = baseInstruments.map(conf => {
                        const col = columns.findIndex(c => c.key === `ra_${ra}_inst_${conf.instrument}`) + 1;
                        if (col === 0) return null;
                        const colLetter = worksheet.getColumn(col).letter;

                        const recs = configItems.filter(c => c.isRecovery && c.recoveryType !== 'final_grade' && (c.targetInstrument === conf.instrument || (!c.targetInstrument && conf.instrument === 'Prueba Practica')));

                        let currentExpr = `${colLetter}${rowIndex + 3}`;
                        recs.forEach(rec => {
                            const recCol = columns.findIndex(c => c.key === `ra_${ra}_inst_${rec.instrument}`) + 1;
                            if (recCol === 0) return;
                            const recColLetter = worksheet.getColumn(recCol).letter;
                            const recCell = `${recColLetter}${rowIndex + 3}`;

                            if (rec.recoveryType === 'max') {
                                currentExpr = `MAX(${currentExpr}, ${recCell})`;
                            } else if (rec.recoveryType === 'average') {
                                currentExpr = `((${currentExpr} + ${recCell}) / 2)`;
                            } else if (rec.recoveryType === 'replace') {
                                currentExpr = `${recCell}`;
                            }
                        });

                        return `(${currentExpr} * ${conf.pes / 100})`;
                    }).filter(Boolean);

                    let baseFormula = `SUM(${formulaParts.join(",")})`;

                    if (finalRec) {
                        const recCol = columns.findIndex(c => c.key === `ra_${ra}_inst_${finalRec.instrument}`) + 1;
                        if (recCol > 0) {
                            const recColLetter = worksheet.getColumn(recCol).letter;
                            const recCell = `${recColLetter}${rowIndex + 3}`;
                            const cap = (finalRec.notaMaxima !== null && finalRec.notaMaxima !== undefined) ? finalRec.notaMaxima : null;
                            const cappedExpr = cap !== null ? `MIN(${recCell}, ${cap})` : recCell;
                            baseFormula = `IF(${cappedExpr}>${baseFormula}, ${cappedExpr}, ${baseFormula})`;
                        }
                    }

                    const mins = configItems.filter(c => !c.isRecovery && c.minim > 0).map(conf => {
                        const col = columns.findIndex(c => c.key === `ra_${ra}_inst_${conf.instrument}`) + 1;
                        const colLetter = worksheet.getColumn(col).letter;
                        return `IF(${colLetter}${rowIndex + 3} < ${conf.minim}, 1, 0)`;
                    });

                    if (mins.length > 0 && finalRec) {
                        const recCol = columns.findIndex(c => c.key === `ra_${ra}_inst_${finalRec.instrument}`) + 1;
                        if (recCol > 0) {
                            const recColLetter = worksheet.getColumn(recCol).letter;
                            const recCell = `${recColLetter}${rowIndex + 3}`;
                            const cap2 = (finalRec.notaMaxima !== null && finalRec.notaMaxima !== undefined) ? finalRec.notaMaxima : null;
                            const cappedExpr2 = cap2 !== null ? `MIN(${recCell}, ${cap2})` : recCell;
                            baseFormula = `IF(${cappedExpr2}>SUM(${formulaParts.join(",")}), ${baseFormula}, IF(OR(${mins.join(",")}), MIN(4, ${baseFormula}), ${baseFormula}))`;
                        } else {
                            baseFormula = `IF(OR(${mins.join(",")}), MIN(4, ${baseFormula}), ${baseFormula})`;
                        }
                    } else if (mins.length > 0) {
                        baseFormula = `IF(OR(${mins.join(",")}), MIN(4, ${baseFormula}), ${baseFormula})`;
                    }
                    dataRow[`ra_${ra}`] = { formula: baseFormula };
                } else {
                    dataRow[`ra_${ra}`] = row[`ra_${ra}`]?.value ?? null;
                }

                const raCol = columns.findIndex(c => c.key === `ra_${ra}`) + 1;
                const raColLetter = worksheet.getColumn(raCol).letter;
                const targetCell = `${raColLetter}${rowIndex + 3}`;
                dataRow[`ra_${ra}_round`] = { formula: `IF(${targetCell} < 5, ROUNDDOWN(${targetCell}, 0), ROUND(${targetCell}, 0))` };
            });

            const addedRow = worksheet.addRow(dataRow);

            addedRow.eachCell((cell, colNumber) => {
                const colKey = columns[colNumber - 1].key;
                if (colKey !== 'alumne') {
                    cell.alignment = { horizontal: 'center' };
                    cell.numFmt = '0.00';
                }
            });
        });

        worksheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 2 }];

        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
        headerRow.alignment = { horizontal: 'center' };

        let currentPos = 1 + (excelData.practiques || []).length;
        ras.forEach((ra, idx) => {
            const configItems = raConfig.filter((c: any) => c.nom_ra === ra);
            let raInstrumentsCount = 0;
            if (configItems.length > 0) {
                raInstrumentsCount = configItems.length;
            } else {
                const practicesWithRa = (excelData.practiques || []).filter((p: any) => p.ras?.some((r: any) => r.nom === ra));
                raInstrumentsCount = Array.from(new Set(practicesWithRa.map((p: any) => p.inst))).filter(Boolean).length;
            }

            const totalCols = raInstrumentsCount + 2;
            const color = raColors[idx % raColors.length];

            for (let i = 0; i < totalCols; i++) {
                const col = worksheet.getColumn(currentPos + 1 + i);
                col.eachCell((cell, rowNumber) => {
                    if (rowNumber > 0) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
                    }
                });
            }
            currentPos += totalCols;
        });

        worksheet.addConditionalFormatting({
            ref: `B3:${worksheet.getColumn(columns.length).letter}${excelRows.length + 2}`,
            rules: [
                {
                    priority: 1,
                    type: 'cellIs',
                    operator: 'lessThan',
                    formulae: ['5'],
                    style: { font: { color: { argb: 'FFFF0000' }, bold: true } }
                },
                {
                    priority: 2,
                    type: 'cellIs',
                    operator: 'greaterThanOrEqual' as any,
                    formulae: ['5'],
                    style: { font: { color: { argb: 'FF00B050' }, bold: true } }
                }
            ]
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `notes_bbdd_aula_${selectedAula}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <>
            <AppSider currentVal={4} setPantalla={setPantalla} />
            {selectedAula && excelData && <ConfiguracioModal
                visible={configModalVisible}
                onCancel={() => {
                    setConfigModalVisible(false);
                    onSelectAula(selectedAula);
                }}
                aulaId={selectedAula + ""}
                ras={excelData?.ras_aula?.map((r: any) => r.nom) || []}
                availableAulas={aulaOptions}
            />}
            <Content style={{ background: '#f0f2f5', padding: '16px 20px', minHeight: '100vh' }}>
                {/* Toolbar */}
                <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} bodyStyle={{ padding: '12px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <Select
                            placeholder="Seleccionar aula..."
                            style={{ width: 280 }}
                            allowClear
                            value={selectedAula}
                            onChange={onSelectAula}
                        >
                            {aulaOptions.map(a => (<Option key={a.id} value={a.id}>{a.nom}</Option>))}
                        </Select>

                        {selectedAula && excelData && (
                            <>
                                <Divider type="vertical" style={{ height: 28 }} />
                                <Tooltip title="Configuración de evaluación">
                                    <Button icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)} />
                                </Tooltip>
                                <Divider type="vertical" style={{ height: 28 }} />
                                <Space>
                                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Prácticas:</span>
                                    <Switch
                                        size="small"
                                        checked={showPractiques}
                                        onChange={setShowPractiques}
                                        checkedChildren="ON"
                                        unCheckedChildren="OFF"
                                    />
                                </Space>
                                {(selectedRA || selectedInst) && (
                                    <Tag
                                        closable
                                        onClose={() => { setSelectedRA(null); setSelectedInst(null); }}
                                        color="blue"
                                        style={{ fontSize: 12 }}
                                    >
                                        {selectedRA && `RA ${selectedRA}`}{selectedInst && ` · ${selectedInst}`}
                                    </Tag>
                                )}
                                <div style={{ flex: 1 }} />
                                <Button icon={<BiDownload />} type="primary" onClick={downloadExcel}>
                                    Exportar Excel
                                </Button>
                            </>
                        )}
                    </div>
                </Card>

                {/* General view (no aula selected) */}
                {!selectedAula && (
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Table
                            size="small"
                            bordered={false}
                            {...tableProps}
                            pagination={false}
                            columns={columns}
                            dataSource={dades}
                            rowKey="id_usuari"
                            expandable={{
                                expandedRowRender: (record: any) => (
                                    <Table size="small" columns={subColumns} dataSource={subData[record.id_usuari] || []} rowKey="id_practica" pagination={false} bordered />
                                ),
                                expandedRowKeys,
                                onExpand: handleExpand,
                                expandIcon: ({ expanded, onExpand, record }) => (
                                    <BsEye style={{ cursor: "pointer", fontSize: "18px", color: expanded ? JutgeColors.colors.color4 : "inherit" }} onClick={(e: any) => onExpand(record, e)} />
                                )
                            }}
                        />
                    </Card>
                )}

                {/* Grade table */}
                {selectedAula && excelData && (
                    <Card
                        bordered={false}
                        style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                        bodyStyle={{ padding: 0, overflow: 'hidden', borderRadius: 12 }}
                    >
                        <Table
                            bordered
                            size="small"
                            columns={excelColumns}
                            dataSource={excelRows}
                            pagination={false}
                            scroll={{ x: 'max-content', y: 'calc(100vh - 200px)' }}
                            rowClassName={(_, idx) => idx % 2 === 0 ? '' : 'table-row-alt'}
                        />
                    </Card>
                )}
            </Content>

            {(user.tipus === "1" || user.tipus === "0") && !loading && (
                <FloatButton
                    shape="circle"
                    onClick={checkDades}
                    style={{ insetInlineStart: 97, backgroundColor: AvaluacioColors.colors.color6 }}
                    icon={<BiRefresh />}
                />
            )}
        </>
    );
};

export default Notes;
